import { createContext, useContext, useMemo, useState } from 'react';
import { Accessibility, Dialog, Table, TableBehaviorProps } from '@fluentui/react-northstar';

import Draggable from 'react-draggable';

import { collectionHasActions } from './Actions';
import { UseInfiniteSource } from './Source';
import { TableRowData } from './Table';

export const EditDialogContext = createContext<EditDialogContextValues | void>(void 0);

export function useEditDialogContext() {

	const context = useContext(EditDialogContext);

	if (typeof context === 'undefined') {
		throw new Error('useEditDialogContext must be used within a <EditDialog /> within <ActionableTable />');
	}

	return context;
}

type EditDialogContextValues = {
	setDialogName: Function,
	setDialogOpen: Function,
	setDialogContent: Function,
	setDialogFooter: Function
}

type ActionableTableProps = React.PropsWithChildren<{
	accessibility: Accessibility<TableBehaviorProps>,
	count: number | undefined,
	style: React.CSSProperties | undefined,
	collection: string,
	rows: TableRowData[],
	source: UseInfiniteSource
}>;

const ActionableContext = createContext<{ rows: TableRowData[]; source: UseInfiniteSource | null }>({ rows: [], source: null });

export function useActionableContext() {
	return useContext(ActionableContext);
}

export function ActionableTable({ children, accessibility, count, style, collection, rows, source }: ActionableTableProps) {

	if (collectionHasActions(collection)) {

		const contextValue = { rows, source };

		return (
			<ActionableContext.Provider value={contextValue}>
				<Table accessibility={accessibility} aria-rowcount={count} style={style}>
					<EditDialog>
						{children}
					</EditDialog>
				</Table>
			</ActionableContext.Provider>
		);
	}

	return (
		<Table accessibility={accessibility} aria-rowcount={count} style={style}>
			{children}
		</Table>
	);
}

export function EditDialog({ children }: React.PropsWithChildren<{}>) {

	const [dialogOpen, setDialogOpen] = useState(false);
	const [dialogName, setDialogName] = useState('');
	const [dialogContent, setDialogContent] = useState<React.ReactNode>(null);
	const [dialogFooter, setDialogFooter] = useState<React.ReactNode>(null);

	const contextValue = useMemo(() => ({
		setDialogName,
		setDialogOpen,
		setDialogContent,
		setDialogFooter
	}), []);

	const itemStyles = useMemo(() => ({
		width: '40vw',
		'text-overflow': 'ellipsis',
		'white-space': 'nowrap',
		overflow: 'hidden'
	}), []);

	useMemo(()=>{
		if(!dialogOpen){
			setDialogFooter(null);
		}
	}, [dialogOpen]);

	return (
		<EditDialogContext.Provider value={contextValue}>
			<Draggable handle=".drag-handle">
				<Dialog
					header={{
						styles: itemStyles,
						content: dialogName,
						className: 'drag-handle'
					}}
					open={dialogOpen}
					content={dialogContent}
					footer={dialogFooter} />
			</Draggable>
			{children}
		</EditDialogContext.Provider>
	);
}
